import React, { createContext, useContext, useState, useEffect } from "react";
import { appConfig } from "../appConfig";

interface ColorContextType {
  themeColor: string;
  setThemeColor: (color: string) => void;
  logoName: string;
  setLogoName: (name: string) => void;
}

const ColorContext = createContext<ColorContextType | undefined>(undefined);

export const useColor = () => {
  const context = useContext(ColorContext);
  if (context === undefined) {
    throw new Error("useColor must be used within a ColorProvider");
  }
  return context;
};

interface ColorProviderProps {
  children: React.ReactNode;
}

export const ColorProvider: React.FC<ColorProviderProps> = ({ children }) => {
  // Session-scoped overrides: reset when tab/session ends
  const [themeColor, setThemeColor] = useState(() => {
    const saved = sessionStorage.getItem("themeColor");
    return saved || appConfig.themeColor;
  });
  const [logoName, setLogoName] = useState(() => {
    const saved = sessionStorage.getItem("logoName");
    return saved || appConfig.name;
  });

  // Apply theme vars and persist for this session
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--theme-color", themeColor);
    root.style.setProperty("--primary", themeColor);
    root.style.setProperty("--accent", themeColor);
    root.style.setProperty("--destructive", themeColor);
    sessionStorage.setItem("themeColor", themeColor);
  }, [themeColor]);

  // Persist logo name for this session
  useEffect(() => {
    sessionStorage.setItem("logoName", logoName);
  }, [logoName]);

  const value = { themeColor, setThemeColor, logoName, setLogoName };

  return (
    <ColorContext.Provider value={value}>{children}</ColorContext.Provider>
  );
};
